import _ from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import { SETTINGS_OPTION_LIST_VIEW } from "../../constants/identifiers";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { asDropTarget } from "..//dragAndDrop/DroppableComponent";
import SettingsDraggableOptionRow from "./SettingsDraggableOptionRow";

const canDropOnComponent = (props, dragItem) => {
  return dragItem.sourceView === props.viewId && dragItem.sourceViewType == SETTINGS_OPTION_LIST_VIEW;
};

const dropCallbacks = {
  onEnter: (_props, _dragItem, _dropTarget) => {
    // Do nothing
  },

  onLeave: (props, dragItem, dropTarget) => {
    return dropTarget.component.onLeave(props, dragItem, dropTarget);
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    return [dragItem.itemId];
  },

  getDragStartParams(_props, _dragItem, _positions, dropTarget) {
    return dropTarget.component.getDragStartParams();
  },

  onHover: (props, dragItem, positions, dropTarget) => {
    return canDropOnComponent(props, dragItem)
      ? dropTarget.component.onHover(props, dragItem, positions, dropTarget)
      : null;
  },

  onDrop: (props, dragItem, positions, dropTarget) => {
    return canDropOnComponent(props, dragItem)
      ? dropTarget.component.onDrop(props, dragItem, positions, dropTarget)
      : null;
  },

  getDropPreviewForItem: (_props, _dragItem, _dropTarget) => {
    return null;
  }
};

class SettingsOptionList extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      dragIndex: null,
      dropIndex: null
    };
    this.renderItem = this.renderItem.bind(this);
    this._domNode = null;
    this._scrollingTimer = null;
    this._scrollStep = 0;
  }

  componentDidMount() {
    this.props.registerDropTarget(this, dropCallbacks);
  }

  getDragStartParams() {}

  onHover(_props, dragItem, positions, _dropTarget) {
    // Since we truncate text rather than wrap, we can safely assume that all items are the same
    // size.
    const itemHeight = positions.sourceRect.height;

    // Work out where it is currently positioned
    const currentIndex = _.isNil(this.state.dropIndex) ? dragItem.index : this.state.dropIndex;

    const numChildren = this.props.items.size;

    // Work out which index it is at based on its position
    let dropIndex = Math.ceil(positions.screenPointerOffset.y / itemHeight) + dragItem.index;

    // Clamp dropIndex within range
    if (dropIndex < 0) {
      dropIndex = 0;
    } else if (dropIndex > numChildren) {
      dropIndex = numChildren;
    }
    if (currentIndex < dropIndex) {
      dropIndex -= 1;
    }
    // And record that position in our state so we can animate the update
    this.setState({
      dragIndex: dragItem.index,
      dropIndex
    });
  }

  onLeave(_props, _dragItem, _dropTarget) {
    this.setState({
      dragIndex: null,
      dropIndex: null
    });
  }

  onDrop(_props, dragItem, _positions, _dropTarget) {
    if (!_.isNil(this.state.dropIndex) && this.props.onMoveItemAtIndex) {
      this.props.onMoveItemAtIndex(dragItem.index, this.state.dropIndex);
    }
    // Now move the dragged item to the correct index...
    this.setState({
      dragIndex: null,
      dropIndex: null
    });
  }

  renderItem(item, index) {
    let slideUp = false;
    let slideDown = false;
    if (!_.isNil(this.state.dragIndex) && !_.isNil(this.state.dropIndex)) {
      // Dragging down
      if (this.state.dragIndex < this.state.dropIndex) {
        slideUp = index > this.state.dragIndex && index <= this.state.dropIndex;
      }
      if (this.state.dragIndex > this.state.dropIndex) {
        slideDown = index < this.state.dragIndex && index >= this.state.dropIndex;
      }
    }

    const cloneItem = React.cloneElement(item, {
      slideUp,
      slideDown
    });

    return cloneItem;
  }

  render() {
    const dragInProgress = !_.isNil(this.state.dragIndex);
    const rows = this.props.items
      ? this.props.items
        .map((option, index) =>
          this.renderItem(
            <SettingsDraggableOptionRow
              key={option.id}
              label={option.label}
              index={index}
              viewId={this.props.viewId}
              onChange={newValue => this.props.onRenameItem(option.id, newValue)}
              onDelete={() => this.props.onDeleteItem(option.id)}
              canDrag={true}
              canDelete={true}
              useContainerWidth={this.props.useContainerWidth}
              autoFocusOnEmpty={false}
            />,
            index
          )
        )
        .toArray()
      : [];

    return (
      <div
        className={`settingsOptionList ${dragInProgress ? "dragInProgress" : ""}`}
        ref={node => (this._domNode = node)}
      >
        {rows}
        <SettingsDraggableOptionRow
          key={`addNew-${this.props.items.size}`}
          label=""
          placeholder="Type to add..."
          index={this.props.items.count()}
          viewId={this.props.viewId}
          onChange={this.props.onAddItem}
          onDelete={null}
          canDrag={false}
          canDelete={false}
          useContainerWidth={this.props.useContainerWidth}
          autoFocusOnEmpty={preprocessorQuery.isMobile() ? false : true}
        />
      </div>
    );
  }
}

SettingsOptionList.propTypes = {
  items: PropTypes.object.isRequired,
  showAdd: PropTypes.bool,
  useContainerWidth: PropTypes.bool,

  onRenameItem: PropTypes.func.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
  onAddItem: PropTypes.func,
  onMoveItemAtIndex: PropTypes.func,

  viewId: PropTypes.string.isRequired,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired
};

export default asDropTarget(SettingsOptionList, SETTINGS_OPTION_LIST_VIEW);
